/**
 * Label — React (web) implementation.
 *
 * All styling references the shared constants in Label.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import type { LabelProps } from './Label';
import {
  labelColorsFor,
  LABEL_TYPOGRAPHY,
  LABEL_RADIUS,
  iconSizeForLabelSize,
  LABEL_HORIZONTAL_PADDING,
  LABEL_VERTICAL_PADDING,
  LABEL_ICON_TEXT_GAP,
  LABEL_TEXT_ONLY_GAP,
  LABEL_ICON_ONLY_SIZE,
  LABEL_ICON_ONLY_LARGE_SIZE,
  LABEL_ICON_ONLY_LARGE_RADIUS,
  LABEL_CIRCLE_RADIUS,
  labelCircleDiameterFor,
} from './Label';

export interface WebLabelProps extends Omit<LabelProps, 'testID' | 'accessibilityLabel'> {
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Label({
  label,
  iconName,
  icon = false,
  size = 'sm',
  tone = 'light',
  backgroundColor,
  textColor,
  width,
  shape = 'rectangle',
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebLabelProps) {
  const resolvedColors = labelColorsFor(backgroundColor, textColor, tone);
  const iconSize = iconSizeForLabelSize[size];
  const isLarge = size === 'l';
  const borderRadius = isLarge ? LABEL_ICON_ONLY_LARGE_RADIUS : LABEL_RADIUS;
  const isIconOnly = icon && (size === 'm' || isLarge);
  const hasIcon = icon && iconName;
  const hasText = !isIconOnly && label;
  const isDecorative = isIconOnly && !ariaLabel;

  const iconOnlySize = isLarge ? LABEL_ICON_ONLY_LARGE_SIZE : LABEL_ICON_ONLY_SIZE;
  const isCircle = !isIconOnly && shape === 'circle';
  const circleDiameter = labelCircleDiameterFor(width);
  const containerStyle: React.CSSProperties = isIconOnly
    ? {
        display: 'inline-flex',
        width: iconOnlySize,
        height: iconOnlySize,
        borderRadius,
        backgroundColor: resolvedColors.background,
        alignItems: 'center',
        justifyContent: 'center',
      }
    : isCircle
      ? {
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          width: circleDiameter,
          height: circleDiameter,
          boxSizing: 'border-box',
          borderRadius: LABEL_CIRCLE_RADIUS,
          backgroundColor: resolvedColors.background,
          gap: hasIcon ? LABEL_ICON_TEXT_GAP : LABEL_TEXT_ONLY_GAP,
        }
      : {
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: width !== undefined ? 'center' : undefined,
          paddingLeft: LABEL_HORIZONTAL_PADDING,
          paddingRight: LABEL_HORIZONTAL_PADDING,
          paddingTop: LABEL_VERTICAL_PADDING,
          paddingBottom: LABEL_VERTICAL_PADDING,
          borderRadius,
          backgroundColor: resolvedColors.background,
          gap: hasIcon ? LABEL_ICON_TEXT_GAP : LABEL_TEXT_ONLY_GAP,
          ...(width !== undefined ? { width, boxSizing: 'border-box' } : {}),
        };

  const textStyle: React.CSSProperties = {
    fontFamily: LABEL_TYPOGRAPHY.fontFamily,
    fontWeight: LABEL_TYPOGRAPHY.fontWeight,
    fontSize: LABEL_TYPOGRAPHY.fontSize,
    lineHeight: `${LABEL_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: LABEL_TYPOGRAPHY.letterSpacing,
    color: resolvedColors.text,
  };

  const iconStyle: React.CSSProperties = {
    width: iconSize,
    height: iconSize,
    color: resolvedColors.icon,
  };

  return (
    <span
      style={containerStyle}
      data-testid={testId}
      role={isIconOnly && ariaLabel ? 'img' : isIconOnly ? undefined : 'text'}
      aria-label={ariaLabel ?? label}
      aria-hidden={isDecorative || undefined}
    >
      {hasIcon && (
        <span style={iconStyle} aria-hidden>
          <Icon name={iconName} size={iconSize} color={resolvedColors.icon} aria-hidden />
        </span>
      )}
      {hasText && <span style={textStyle}>{label}</span>}
    </span>
  );
}
